import React from 'react';
import { ListGroup, Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import './TripInfo.scss';
import { useSelector } from 'react-redux';
import { TripState } from '../../../../../types/trip.store.type';
import moment from 'moment';
import { Guests, ContractAgreementRequiredAction } from 'guest-data-types-ts-lib';

import { FormattedDate } from 'react-intl';
import { IconPeopleTwo, IconHandshake, IconMoneyBill, IconCheckMark, IconArrowRightTwo, IconDog } from '../../../../../assets/icons';
import { AppRoutes } from '../../../../../routes';
import { TranslationUtils } from '../../../../../utils';

interface ManagedItem {
    id: string;
    icon: any;
    category: any;
    title: any;
    subtitle?: any;
    completed: boolean | null;
    link?: string | null;
    showPointer: boolean;
}

export const TripInfo: React.FC = () => {
    const trip = useSelector((store: { trip: TripState }) => store.trip);

    const history = useHistory();

    const { guests, stay, required_actions, reservation } = trip;

    if (!guests || !stay || !required_actions || !reservation) {
        return null;
    }

    const items: ManagedItem[] = [];

    const getGuestsItem = (tripGuests: Guests): ManagedItem => {
        const { current_counts } = tripGuests;
        const currentOccupancy = current_counts.adults + current_counts.children;
        const hasChildren = current_counts.children !== 0;
        const adultsSubtitle = `${current_counts.adults} ${TranslationUtils.get(
            current_counts.adults === 1 ? 'adult' : 'adults'
        ).toLocaleLowerCase()}`;

        return {
            id: 'trip-guests',
            icon: <IconPeopleTwo className="category-icon" />,
            category: TranslationUtils.get('guests'),
            title: `${currentOccupancy} ${TranslationUtils.get(`${currentOccupancy === 1 ? 'guest' : 'guests'}`).toLocaleLowerCase()}`,
            subtitle: hasChildren
                ? `${adultsSubtitle} ⋅ ${current_counts.children} ${TranslationUtils.get(
                      current_counts.children === 1 ? 'child' : 'children'
                  ).toLocaleLowerCase()}`
                : adultsSubtitle,
            completed: null,
            showPointer: false,
        };
    };

    const updatePetsItems = (tripGuests: Guests): ManagedItem => {
        const { current_counts } = tripGuests;
        const currentPetOccupancy = current_counts.pets;

        const getCurrentOccupancyTitle = (): string => {
            return `${currentPetOccupancy} ${TranslationUtils.get(`${currentPetOccupancy === 1 ? 'pet' : 'pets'}`).toLocaleLowerCase()}`;
        };

        return {
            id: 'trip-pets',
            icon: <IconDog className="category-icon" />,
            category: TranslationUtils.get('pets'),
            title: getCurrentOccupancyTitle(),
            completed: null,
            showPointer: false,
        };
    };

    const getRentalAgreementItem = (agreement: ContractAgreementRequiredAction): ManagedItem => {
        const isAgreementComplete = agreement.completed && !!agreement.completed_date;

        let agreementTitle;
        let agreementSubtitle;

        const signedOn = (
            <FormattedDate
                value={moment(new Date(agreement.completed_date as string)).toDate()}
                day="numeric"
                month="short"
                year="numeric"
            />
        );
        agreementTitle = TranslationUtils.get('view_terms');
        agreementSubtitle = TranslationUtils.get('signed_on', { signedOn });

        return {
            id: 'trip-rental-agreement',
            icon: <IconHandshake className="category-icon" />,
            category: TranslationUtils.get('rental_terms'),
            title: agreementTitle,
            subtitle: agreementSubtitle,
            completed: isAgreementComplete,
            link: AppRoutes.RENTAL_AGREEMENT,
            showPointer: true,
        };
    };

    const getPaymentDetailsItem = (): ManagedItem => {
        const paymentTitle = TranslationUtils.get('view_details');
        const link = AppRoutes.RECEIPT;

        return {
            id: 'trip-pricing-details',
            icon: <IconMoneyBill className="category-icon" />,
            category: TranslationUtils.get('pricing_details'),
            title: paymentTitle,
            completed: null,
            link,
            showPointer: true,
        };
    };

    items.push(getGuestsItem(guests));

    const reservationHasDogs = guests.current_counts.pets > 0;
    if (reservationHasDogs) {
        items.push(updatePetsItems(guests));
    }
    // TODO: These should always be displayed
    trip.required_actions?.contract_agreement && items.push(getRentalAgreementItem(trip.required_actions.contract_agreement));
    items.push(getPaymentDetailsItem());

    return (
        <Card className="panel manage-trip-panel">
            <ListGroup variant="flush">
                {items.map((item, i) => (
                    <ListGroup.Item key={i}>
                        <div
                            tabIndex={0}
                            role="button"
                            className="managed-item"
                            id={item.id}
                            onKeyPress={() => item.link && history.push(item.link)}
                            onClick={() => item.link && history.push(item.link)}
                        >
                            <div className="category">
                                <div className="d-none d-md-block"> {item.icon}</div>
                                <span className="body-regular">{item.category}</span>
                            </div>
                            <div className="item-notification">
                                {item.completed === true ? (
                                    <div className="complete-notification">
                                        <IconCheckMark />
                                    </div>
                                ) : item.completed === false ? (
                                    <div className="circle-notification" />
                                ) : null}
                            </div>
                            <div className="block">
                                <p className="body-regular title">{item.title}</p>
                                {item.subtitle && <span className="body-xs subtitle">{item.subtitle}</span>}
                            </div>
                            {item.showPointer && (
                                <div className="item-next">
                                    <IconArrowRightTwo className="pointer" />
                                </div>
                            )}
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>
        </Card>
    );
};
